import type { SimpleSelectable, Chemical, ProductChemicalComposition } from '../types/db.ts';
import { ChemicalExemptionTypeIds } from './exemptions.ts';
import { UsageTypeIds } from './usage-types.ts';
import type { ChemicalEditing } from '../types/chemical-types.ts';
import { POUNDS_PER_GALLON_WATER } from './formulas/units.ts';

/**
 * The concentration of a chemical in a product as stated by the product's data sheet, with no regulatory adjustments.
 * This is the right starting point for every app except TRI, which must use `compositionConcentrationTri`.
 */
export type ConcentrationBase = number & { _tag: 'ConcentrationBase' };

/**
 * The concentration of a chemical in a product after applying TRI's chemical exemptions (de minimis, non-airborne
 * sulfuric acid, etc). These exemptions have no basis outside of TRI, so this must never be used by other apps.
 */
export type ConcentrationTri = number & { _tag: 'ConcentrationTri' };

export type CompositionPercentages = Pick<
  SimpleSelectable<ProductChemicalComposition>,
  'concentrationSolo' | 'concentrationMin' | 'concentrationMax'
>;

export type CompositionForTri = Pick<
  SimpleSelectable<ProductChemicalComposition>,
  | 'chemicalExemptionID'
  | 'concentrationSolo'
  | 'concentrationMin'
  | 'concentrationMax'
  | 'decimalPlacesOfPrecision'
  | 'usageTypeID'
>;

export function chemicalConcentrationEditingToSaved(
  specificGravity: number | '' | null,
  editing: Omit<ChemicalEditing, 'productChemicalCompositionID'>,
):
  | { status: 'error'; error: string }
  | {
      status: 'success';
      concentrationSolo: number | null;
      concentrationMin: number | null;
      concentrationMax: number | null;
    } {
  const percentage = editing.percentage;
  let basics: {
    concentrationSolo: number | null;
    concentrationMin: number | null;
    concentrationMax: number | null;
  };

  if (percentage.type === 'solo') {
    if (percentage.solo === '') {
      return { status: 'error', error: `Expected number for concentration, got blank field instead.` };
    }

    if (percentage.solo <= 0 || percentage.solo > 100) {
      return { status: 'error', error: `Expected number for concentration to be between 0 and 100.` };
    }

    basics = {
      concentrationSolo: percentage.solo / 100,
      concentrationMin: null,
      concentrationMax: null,
    };
  } else if (percentage.type === 'minMax') {
    if (percentage.min === '') {
      return { status: 'error', error: `Expected number for concentration minimum, got blank field instead.` };
    }

    if (percentage.min <= 0 || percentage.min > 100) {
      return { status: 'error', error: `Expected number for concentration minimum to be between 0 and 100.` };
    }

    if (percentage.max === '') {
      return { status: 'error', error: `Expected number for concentration maximum, got blank field instead.` };
    }

    if (percentage.max <= 0 || percentage.max > 100) {
      return { status: 'error', error: `Expected number for concentration maximum to be between 0 and 100.` };
    }

    basics = {
      concentrationSolo: null,
      concentrationMin: percentage.min / 100,
      concentrationMax: percentage.max / 100,
    };
  } else if (percentage.type === 'soloPoundsPerGallon') {
    if (specificGravity === '' || specificGravity === null || specificGravity <= 0) {
      return {
        status: 'error',
        error: `Expected valid specific gravity in order to compute lb/gal concentration as percentage.`,
      };
    }

    if (percentage.solo === '') {
      return { status: 'error', error: `Expected number for concentration, got blank field instead.` };
    }

    const concentrationSolo = poundPerGallonToPercentage(specificGravity, percentage.solo) / 100;

    if (concentrationSolo <= 0 || concentrationSolo > 1) {
      return { status: 'error', error: `Expected number for concentration to be between 0 and 100% of density.` };
    }

    basics = {
      concentrationSolo,
      concentrationMin: null,
      concentrationMax: null,
    };
  } else {
    if (specificGravity === '' || specificGravity === null || specificGravity <= 0) {
      return {
        status: 'error',
        error: `Expected valid specific gravity in order to compute lb/gal concentration as percentage.`,
      };
    }

    if (percentage.min === '') {
      return { status: 'error', error: `Expected number for concentration minimum, got blank field instead.` };
    }

    if (percentage.max === '') {
      return { status: 'error', error: `Expected number for concentration maximum, got blank field instead.` };
    }

    const concentrationMin = poundPerGallonToPercentage(specificGravity, percentage.min) / 100;
    const concentrationMax = poundPerGallonToPercentage(specificGravity, percentage.max) / 100;

    if (concentrationMin <= 0 || concentrationMin > 1) {
      return {
        status: 'error',
        error: `Expected number for concentration minimum to be between 0% and 100% of density.`,
      };
    }

    if (concentrationMax <= 0 || concentrationMax > 1) {
      return {
        status: 'error',
        error: `Expected number for concentration maximum to be between 0% and 100% of density.`,
      };
    }

    basics = {
      concentrationSolo: null,
      concentrationMin,
      concentrationMax,
    };
  }

  return {
    status: 'success',
    ...basics,
  };
}

export function chemicalConcentrationComputeAverageUsingDeMinimis(inputs: {
  concentrationSolo: number | null;
  concentrationMin: number | null;
  concentrationMax: number | null;
  deMinimis: number; // If unknown, use 0.
  decimalPlacesOfPrecision: 1 | 2 | 3 | 4 | 5;
}): number {
  // Brandon mentioned to me that the EPA does not consider a minimum of "0" to be valid for averaging or de minimis,
  // so we should just use the max as the concentration in that case:
  if (
    typeof inputs.concentrationMin === 'number' &&
    typeof inputs.concentrationMax === 'number' &&
    typeof inputs.concentrationSolo !== 'number' &&
    inputs.concentrationMin === 0
  ) {
    inputs = {
      ...inputs,
      concentrationSolo: inputs.concentrationMax,
      concentrationMin: null,
      concentrationMax: null,
    };
  }

  if (inputs.concentrationSolo !== null) {
    if (inputs.concentrationSolo < inputs.deMinimis) {
      return 0;
    }

    return inputs.concentrationSolo;
  }

  if (typeof inputs.concentrationMin === 'number' && typeof inputs.concentrationMax === 'number') {
    if (inputs.concentrationMax < inputs.deMinimis) {
      return 0;
    }

    // If we aren't straddling de minimis, just return the average of the two numbers
    if (inputs.deMinimis <= 0 || inputs.concentrationMin >= inputs.deMinimis) {
      return (inputs.concentrationMin + inputs.concentrationMax) / 2;
    }

    // See "Concentration Ranges Straddling the De Minimis Level" in https://guideme.epa.gov/ords/guideme_ext/guideme_ext/guideme/file/ry_2023_rfi.pdf
    const deMinimisPrecision = 1 / 10 ** (2 + inputs.decimalPlacesOfPrecision);
    const aboveDeMinimisAverage = (inputs.concentrationMax + inputs.deMinimis) / 2;
    const aboveDeMinimisRange = inputs.concentrationMax - (inputs.deMinimis - deMinimisPrecision);
    const concentrationRange = inputs.concentrationMax - inputs.concentrationMin;

    return (aboveDeMinimisRange / concentrationRange) * aboveDeMinimisAverage;

    // The remaining branches shouldn't happen in theory, but could happen in practice in an erroneous situation:
  } else if (typeof inputs.concentrationMin === 'number') {
    return inputs.concentrationMin;
  } else if (typeof inputs.concentrationMax === 'number') {
    return inputs.concentrationMax;
  } else {
    console.warn('chemicalConcentrationComputeAverageUsingDeMinimis: Forced to fallback to 0 concentration.');
    return 0;
  }
}

export function compositionConcentrationBase(composition: CompositionPercentages): ConcentrationBase {
  return chemicalConcentrationComputeAverageUsingDeMinimis({
    concentrationSolo: composition.concentrationSolo,
    concentrationMin: composition.concentrationMin,
    concentrationMax: composition.concentrationMax,
    deMinimis: 0,
    // Precision only matters when straddling de minimis:
    decimalPlacesOfPrecision: 1,
  }) as ConcentrationBase;
}

export function compositionConcentrationTri(
  chemical: Pick<SimpleSelectable<Chemical>, 'deMinimis'>,
  composition: CompositionForTri,
  // TODO: 3. Exemptions - Will need to handle the year once year-specific exemptions exist:
  _year: number,
): ConcentrationTri {
  if (
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonFibrousAluminumOxide ||
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonFumeOrDustAluminum ||
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonFumeOrDustZinc ||
    (composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonManufacturedIsopropylAlcohol &&
      composition.usageTypeID !== UsageTypeIds.Manufactured) ||
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonAirborneSulfuricAcid ||
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonAqueousNonAnhydrousAmmonia ||
    composition.chemicalExemptionID === ChemicalExemptionTypeIds.NonWaterDissociableNitrateCompounds
  ) {
    return 0 as ConcentrationTri;
  }

  const percentage = chemicalConcentrationComputeAverageUsingDeMinimis({
    deMinimis:
      composition.chemicalExemptionID === ChemicalExemptionTypeIds.DeMinimis &&
      composition.usageTypeID !== UsageTypeIds.Manufactured
        ? chemical.deMinimis
        : 0,
    decimalPlacesOfPrecision: composition.decimalPlacesOfPrecision,
    concentrationMin: composition.concentrationMin,
    concentrationMax: composition.concentrationMax,
    concentrationSolo: composition.concentrationSolo,
  });

  if (composition.chemicalExemptionID === ChemicalExemptionTypeIds.AqueousAmmonia) {
    return (percentage * 0.1) as ConcentrationTri;
  }

  return percentage as ConcentrationTri;
}

export function poundPerGallonToPercentage(specificGravity: number, poundsPerGallon: number): number;
export function poundPerGallonToPercentage(specificGravity: number, poundsPerGallon: number | ''): number | '';
export function poundPerGallonToPercentage(specificGravity: number, poundsPerGallon: number | ''): number | '' {
  if (poundsPerGallon === '') {
    return '';
  }
  return (poundsPerGallon / (specificGravity * POUNDS_PER_GALLON_WATER)) * 100;
}
export function percentageToPoundPerGallon(specificGravity: number, percentageZeroToOneHundred: number): number;
export function percentageToPoundPerGallon(
  specificGravity: number,
  percentageZeroToOneHundred: number | '',
): number | '';
export function percentageToPoundPerGallon(
  specificGravity: number,
  percentageZeroToOneHundred: number | '',
): number | '' {
  if (percentageZeroToOneHundred === '') {
    return '';
  }
  return (percentageZeroToOneHundred / 100) * specificGravity * POUNDS_PER_GALLON_WATER;
}
